'use client'
import { useThree, useFrame } from '@react-three/fiber'
import { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three/webgpu'
import {
  Fn,
  vec3,
  vec4,
  pow,
  output,
  screenUV,
  screenSize,
  uniform,
} from 'three/tsl'
import { shadowDitherWGSL, shadowDitherConfig } from './dithering'
import { setDitherPatcher, clearDitherPatcher } from './ditherRegistry'

// ─── Material-level shadow dithering ─────────────────────────────
// Injects a shadow-aware dither into every NodeMaterial's outputNode.
// Lit areas stay clean. Deep shadows get a solid color tint.
// The transition zone between lit and shadow is Bayer-dithered.

let warnedWebGL = false

const RendererDither = ({ onUniforms }) => {
  const { scene, gl } = useThree()

  // The dither body is raw WGSL, and WGSL only exists on the webgpu
  // backend. On the webgl fallback (old phones, blocklisted gpus, ?gl=1)
  // every patched material failed to compile and the whole world rendered
  // as black-and-white silhouettes, which is how one user saw the town. A
  // colour world without the retro grain beats a monochrome one, so on
  // webgl this pass simply stands down.
  const webgpu = gl?.backend?.isWebGPUBackend === true
  if (!webgpu && !warnedWebGL) {
    warnedWebGL = true
    console.warn('[dither] webgl backend, shadow dither disabled (WGSL-only)')
  }

  const uniforms = useMemo(() => ({
    shadowTint: uniform(
      new THREE.Vector3(
        shadowDitherConfig.shadowTint.x,
        shadowDitherConfig.shadowTint.y,
        shadowDitherConfig.shadowTint.z,
      ),
    ),
    shadowThreshold: uniform(shadowDitherConfig.shadowThreshold),
    ditherEdgeWidth: uniform(shadowDitherConfig.ditherEdgeWidth),
    pixelSize: uniform(shadowDitherConfig.pixelSize),
    tintStrength: uniform(shadowDitherConfig.tintStrength),
  }), [])

  useEffect(() => {
    if (onUniforms) onUniforms(uniforms)
  }, [uniforms, onUniforms])

  const ditherNode = useMemo(
    () =>
      Fn(() => {
        const linear = output
        // Linear → sRGB so dithering operates in perceptual space
        const srgb = vec4(
          pow(linear.rgb.max(0.0), vec3(1.0 / 2.2)),
          linear.a,
        )

        const dithered = shadowDitherWGSL({
          inputColor: srgb,
          uvCoord: screenUV,
          resolution: screenSize,
          shadowTint: uniforms.shadowTint,
          shadowThreshold: uniforms.shadowThreshold,
          ditherEdgeWidth: uniforms.ditherEdgeWidth,
          pixelSize: uniforms.pixelSize,
          tintStrength: uniforms.tintStrength,
        })

        // sRGB → Linear for renderer's final color transform
        return vec4(
          pow(dithered.rgb.max(0.0), vec3(2.2)),
          dithered.a,
        )
      })(),
    [uniforms],
  )

  const patched = useRef(new Set())
  const frameCount = useRef(0)

  // The material half on its own, because it is also the registry's
  // contract: components that create materials at runtime (billboards on
  // a poll, drawings hung when their texture lands) call this through
  // ditherRegistry at creation, instead of rendering clean for up to
  // three seconds until the sweep below finds them.
  const patchOneMaterial = (mat) => {
    if (!mat) return
    // outputNode only on webgpu; the nearest-filter pass below is
    // backend-agnostic and keeps the pixel look either way
    if (webgpu && mat.isNodeMaterial && !patched.current.has(mat)) {
      mat.outputNode = ditherNode
      mat.needsUpdate = true
      patched.current.add(mat)
    }
    // Nearest-neighbor filtering on all textures
    const texProps = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap', 'alphaMap']
    for (const prop of texProps) {
      if (mat[prop] && mat[prop].magFilter !== THREE.NearestFilter) {
        mat[prop].magFilter = THREE.NearestFilter
        mat[prop].minFilter = THREE.NearestFilter
        mat[prop].needsUpdate = true
      }
    }
  }

  const patchOneMesh = (obj) => {
    if (!obj.isMesh || !obj.material) return
    patchOneMaterial(obj.material)
  }

  const patchMesh = useRef(patchOneMesh)
  const patchMat = useRef(patchOneMaterial)

  useEffect(() => {
    patchMesh.current = patchOneMesh
    patchMat.current = patchOneMaterial
  }, [ditherNode])

  useEffect(() => {
    scene.traverse(patchMesh.current)

    // The registry hands out a stable wrapper, so late-created materials
    // patch against whatever the current node is.
    const live = (mat) => patchMat.current(mat)
    setDitherPatcher(live)

    return () => {
      clearDitherPatcher(live)
      patched.current.forEach((mat) => {
        mat.outputNode = null
        mat.needsUpdate = true
      })
      patched.current.clear()
    }
  }, [scene, ditherNode])

  // Catch dynamically added objects. Every frame for the first ten
  // seconds, then every ~3 seconds as a safety net for strays.
  //
  // The boot window matters: each model converts its GLB materials in an
  // effect, which runs after commit, so the mesh has been drawn once
  // clean before the old sweep found it at frame 180 and bumped it. Every
  // converted material in the town then recompiled in one frame, about
  // three seconds after the first, which is right when the veil lifts.
  // Patched on the frame after it appears, a material compiles once,
  // dithered, and the veil hides it. The traverse is well under a
  // millisecond, which is what makes per-frame affordable for ten seconds.
  useFrame(() => {
    frameCount.current++
    const n = frameCount.current
    if (n > 600 && n % 180 !== 0) return
    scene.traverse(patchMesh.current)
  })

  return null
}

export default RendererDither
